import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BiologicalLayout from '@/components/layout/BiologicalLayout';
import { useAuth, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft, ChevronLeft, ChevronRight, Bookmark, BookmarkCheck,
  MessageSquare, Headphones, Image, Send, Loader2, Play, Pause,
  Volume2, Clock, Activity, Pill, FileText, ListTodo, ExternalLink
} from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════════
// KNOWLEDGE INTELLIGENCE — Biological Knowledge Delivery System
// ══════════════════════════════════════════════════════════════════════════════

function KnowledgeCard({ item, onClick }) {
  const takeaways = (item.takeaways || []).slice(0, 3);
  const slideCount = item.slide_images?.length || 0;
  const hasAudio = !!item.audio_url;
  const cover = item.cover_image;
  const prog = item.progress;
  const progressLabel = prog?.completed ? 'Completed' : prog ? 'In Progress' : null;
  const progressColor = prog?.completed ? '#4E7A6B' : prog ? '#C89B7B' : null;

  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/')) return url;
    return `${API}/documents/file/${url}`;
  };

  return (
    <div
      onClick={onClick}
      className="group bg-white rounded-2xl border border-[#EBE8E3] overflow-hidden hover:shadow-lg hover:shadow-[#8C6B55]/5 transition-all duration-500 cursor-pointer"
      data-testid={`knowledge-card-${item.id}`}
    >
      {/* Cover Image */}
      {cover ? (
        <div className="relative h-44 bg-[#F4F2EE] overflow-hidden">
          <img
            src={resolveUrl(cover)}
            alt={item.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            loading="lazy"
          />
          <div className="absolute top-3 right-3 flex gap-1.5">
            {slideCount > 1 && (
              <Badge className="bg-black/40 text-white border-0 text-[10px] backdrop-blur-sm">
                <Image className="w-2.5 h-2.5 mr-1" />{slideCount} slides
              </Badge>
            )}
          </div>
          {/* Progress indicator */}
          {progressLabel && (
            <div className="absolute bottom-0 left-0 right-0 h-1" style={{ backgroundColor: progressColor + '30' }}>
              <div className="h-full transition-all" style={{
                width: prog?.completed ? '100%' : `${Math.max(prog?.article_progress_pct || 10, 10)}%`,
                backgroundColor: progressColor,
              }} />
            </div>
          )}
        </div>
      ) : (
        <div className="relative h-32 bg-gradient-to-br from-[#F4F2EE] to-[#EBE8E3] flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-white/60 flex items-center justify-center">
            <Image className="w-6 h-6 text-[#C89B7B]" strokeWidth={1} />
          </div>
          {progressLabel && (
            <div className="absolute bottom-0 left-0 right-0 h-1" style={{ backgroundColor: progressColor + '30' }}>
              <div className="h-full transition-all" style={{
                width: prog?.completed ? '100%' : `${Math.max(prog?.article_progress_pct || 10, 10)}%`,
                backgroundColor: progressColor,
              }} />
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className="p-5 space-y-3">
        {/* Category + indicators */}
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="border-[#EBE8E3] text-[#8A8681] text-[9px] capitalize">{item.category}</Badge>
          {hasAudio && <Badge variant="outline" className="border-[#EBE8E3] text-[#8C6B55] text-[9px]"><Headphones className="w-2.5 h-2.5 mr-0.5" />Audio</Badge>}
          {progressLabel && (
            <Badge variant="outline" className="text-[9px] ml-auto" style={{ borderColor: progressColor + '40', color: progressColor }}>
              {progressLabel}
            </Badge>
          )}
        </div>

        {/* Title */}
        <h3 className="text-base font-['Outfit'] font-medium text-[#2C2A29] leading-snug line-clamp-2" data-testid="card-title">
          {item.title}
        </h3>

        {/* Context line */}
        {item.context_line && (
          <p className="text-xs text-[#8C6B55] font-['Work_Sans'] italic leading-relaxed">
            {item.context_line}
          </p>
        )}

        {/* Relevance badge */}
        {item.relevance_reason && (
          <p className="text-[10px] text-[#5C6B8A] font-['Work_Sans'] flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[#5C6B8A]" />
            {item.relevance_reason}
          </p>
        )}

        {/* Why this matters (short - data-linked) */}
        {item.why_this_matters?.has_data_link && item.why_this_matters?.short && (
          <p className="text-[10px] text-[#4E7A6B] font-['Work_Sans'] flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#4E7A6B]/[0.05]" data-testid="card-why-matters">
            <span className="w-1 h-1 rounded-full bg-[#4E7A6B] flex-shrink-0" />
            {item.why_this_matters.short}
          </p>
        )}

        {/* Takeaways */}
        {takeaways.length > 0 && (
          <div className="space-y-1.5 pt-1">
            {takeaways.map((t, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="w-1 h-1 rounded-full bg-[#C89B7B] mt-1.5 flex-shrink-0" />
                <p className="text-[11px] text-[#5C5955] font-['Work_Sans'] leading-relaxed line-clamp-1">{t}</p>
              </div>
            ))}
          </div>
        )}

        {/* Footer actions */}
        <div className="flex items-center gap-3 pt-2 border-t border-[#EBE8E3]">
          <button className="text-[10px] text-[#5C6B8A] font-['Work_Sans'] font-medium hover:text-[#4C5B7A] transition-colors" data-testid="view-deep-dive">
            View Deep Dive
          </button>
          {hasAudio && (
            <button className="text-[10px] text-[#8C6B55] font-['Work_Sans'] hover:text-[#7C5B45] transition-colors flex items-center gap-1">
              <Headphones className="w-3 h-3" />Listen
            </button>
          )}
          <div className="flex-1" />
          {item.comment_count > 0 && (
            <span className="text-[10px] text-[#8A8681] font-['Work_Sans'] flex items-center gap-1">
              <MessageSquare className="w-3 h-3" />{item.comment_count}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SLIDESHOW CAROUSEL
// ══════════════════════════════════════════════════════════════════════════════

function SlideCarousel({ slides, contentId }) {
  const [current, setCurrent] = useState(0);
  if (!slides?.length) return null;

  const goTo = (idx) => {
    setCurrent(idx);
    if (contentId) {
      axios.post(`${API}/knowledge/progress/${contentId}`, { last_slide_viewed: idx }).catch(() => {});
    }
  };
  const prev = () => goTo(current === 0 ? slides.length - 1 : current - 1);
  const next = () => goTo(current === slides.length - 1 ? 0 : current + 1);

  return (
    <div className="relative rounded-2xl overflow-hidden bg-[#F4F2EE]" data-testid="slide-carousel">
      <div className="aspect-[16/9] relative">
        <img
          src={slides[current]?.startsWith('http') ? slides[current] : slides[current]?.startsWith('/api/') ? slides[current] : `${API}/documents/file/${slides[current]}`}
          alt={`Slide ${current + 1}`}
          className="w-full h-full object-contain bg-[#F4F2EE]"
        />
      </div>
      {slides.length > 1 && (
        <>
          <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow-sm" data-testid="slide-prev">
            <ChevronLeft className="w-5 h-5 text-[#2C2A29]" />
          </button>
          <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow-sm" data-testid="slide-next">
            <ChevronRight className="w-5 h-5 text-[#2C2A29]" />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {slides.map((_, i) => (
              <button key={i} onClick={() => goTo(i)}
                className={`w-2 h-2 rounded-full transition-all ${i === current ? 'bg-[#2C2A29] w-5' : 'bg-[#2C2A29]/25'}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// AUDIO PLAYER
// ══════════════════════════════════════════════════════════════════════════════

function AudioPlayer({ url }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);

  if (!url) return null;
  const src = url.startsWith('http') ? url : url.startsWith('/api/') ? url : `${API}/documents/file/${url}`;

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); } else { audioRef.current.play(); }
    setPlaying(!playing);
  };

  const handleTimeUpdate = () => { if (audioRef.current) setCurrentTime(audioRef.current.currentTime); };
  const handleLoaded = () => { if (audioRef.current) setDuration(audioRef.current.duration); };
  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    if (audioRef.current) { audioRef.current.currentTime = pct * duration; }
  };
  const cycleSpeed = () => {
    const speeds = [1, 1.25, 1.5, 2];
    const next = speeds[(speeds.indexOf(speed) + 1) % speeds.length];
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };
  const fmt = (s) => { const m = Math.floor(s / 60); return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`; };

  return (
    <div className="rounded-xl border border-[#EBE8E3] p-4 bg-[#F8F6F3]" data-testid="audio-player">
      <audio ref={audioRef} src={src} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoaded} onEnded={() => setPlaying(false)} preload="metadata" />
      <div className="flex items-center gap-4">
        <button onClick={togglePlay} className="w-11 h-11 rounded-full bg-[#2C2A29] flex items-center justify-center flex-shrink-0 hover:bg-[#3C3A39] transition-colors" data-testid="audio-play">
          {playing ? <Pause className="w-5 h-5 text-white" fill="white" /> : <Play className="w-5 h-5 text-white ml-0.5" fill="white" />}
        </button>
        <div className="flex-1 space-y-1.5">
          <div className="flex items-center gap-2 text-[10px] text-[#8A8681] font-['Work_Sans']">
            <Headphones className="w-3 h-3" />
            <span>Listen to this topic</span>
            <div className="flex-1" />
            <span>{fmt(currentTime)}</span>
            <span>/</span>
            <span>{fmt(duration)}</span>
          </div>
          <div onClick={handleSeek} className="w-full h-1.5 bg-[#EBE8E3] rounded-full cursor-pointer relative">
            <div className="h-full bg-[#8C6B55] rounded-full transition-all" style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }} />
          </div>
        </div>
        <button onClick={cycleSpeed} className="text-[10px] text-[#5C5955] font-mono px-2 py-1 rounded bg-[#EBE8E3] hover:bg-[#DDD9D4] transition-colors" data-testid="audio-speed">
          {speed}x
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMMENT THREAD
// ══════════════════════════════════════════════════════════════════════════════

function CommentThread({ contentId, userId }) {
  const [comments, setComments] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [posting, setPosting] = useState(false);

  const fetchComments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/knowledge/comments/${contentId}`);
      setComments(res.data || []);
    } catch { /* empty */ }
  }, [contentId]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  const handlePost = async () => {
    if (!newMessage.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${API}/knowledge/comments`, { content_id: contentId, message: newMessage.trim() });
      setNewMessage('');
      toast.success('Comment posted');
      fetchComments();
    } catch { toast.error('Failed to post'); }
    finally { setPosting(false); }
  };

  // Build threaded view
  const rootComments = comments.filter(c => !c.parent_comment_id);
  const replies = comments.filter(c => c.parent_comment_id);
  const getReplies = (parentId) => replies.filter(r => r.parent_comment_id === parentId);

  return (
    <div data-testid="comment-thread">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mb-4">
        Feedback & Questions ({comments.length})
      </p>

      {/* Post new */}
      <div className="flex gap-2 mb-6">
        <Textarea value={newMessage} onChange={e => setNewMessage(e.target.value)}
          placeholder="Ask a question or share feedback..."
          className="bg-white border-[#EBE8E3] text-[#2C2A29] text-xs min-h-[50px] flex-1 resize-none" data-testid="comment-input" />
        <Button onClick={handlePost} disabled={posting || !newMessage.trim()} size="sm"
          className="bg-[#2C2A29] text-white hover:bg-[#3C3A39] h-auto self-end px-3" data-testid="post-comment-btn">
          {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        </Button>
      </div>

      {/* Thread */}
      <div className="space-y-4">
        {rootComments.map(c => (
          <div key={c.id} data-testid={`comment-${c.id}`}>
            <div className="flex items-start gap-3">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-medium ${
                c.author_role === 'subscriber' ? 'bg-[#EBE8E3] text-[#5C5955]' : 'bg-[#8C6B55]/10 text-[#8C6B55]'
              }`}>
                {c.author_name?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-['Work_Sans'] font-medium text-[#2C2A29]">{c.author_name}</span>
                  <span className="text-[9px] text-[#8A8681]">{c.author_role !== 'subscriber' ? c.author_role : ''}</span>
                  {c.resolved && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[8px]">Resolved</Badge>}
                </div>
                <p className="text-xs text-[#4A4745] font-['Work_Sans'] leading-relaxed">{c.message}</p>
                <p className="text-[9px] text-[#8A8681] mt-1">{new Date(c.created_at).toLocaleDateString()}</p>
              </div>
            </div>
            {/* Replies */}
            {getReplies(c.id).map(r => (
              <div key={r.id} className="ml-10 mt-2 pl-3 border-l-2 border-[#EBE8E3]" data-testid={`reply-${r.id}`}>
                <div className="flex items-start gap-2">
                  <div className="w-6 h-6 rounded-full bg-[#8C6B55]/10 flex items-center justify-center flex-shrink-0 text-[9px] text-[#8C6B55] font-medium">
                    {r.author_name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <span className="text-[10px] font-['Work_Sans'] font-medium text-[#8C6B55]">{r.author_name}</span>
                    <p className="text-[11px] text-[#4A4745] font-['Work_Sans'] leading-relaxed">{r.message}</p>
                    <p className="text-[9px] text-[#8A8681] mt-0.5">{new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// CONTENT LINKS — 4-layer data linking
// ══════════════════════════════════════════════════════════════════════════════

const LINK_SECTIONS = [
  { key: 'seen_in_data', label: 'Seen in Your Data', icon: Activity, color: '#5C6B8A' },
  { key: 'medication_links', label: 'Linked to Your Medication', icon: Pill, color: '#8C6B55' },
  { key: 'report_links', label: 'Explore in Your Reports', icon: FileText, color: '#4E7A6B' },
  { key: 'protocol_links', label: 'Your Protocol Is Targeting This', icon: ListTodo, color: '#C89B7B' },
];

function ContentLinksBlock({ links, navigate }) {
  if (!links?.has_links) return null;

  const sections = LINK_SECTIONS.filter(s => (links[s.key] || []).length > 0);
  if (sections.length === 0) return null;

  return (
    <div className="space-y-4 mb-10" data-testid="content-links">
      {sections.map(sec => (
        <div key={sec.key} className="rounded-xl border border-[#EBE8E3] overflow-hidden" data-testid={`link-section-${sec.key}`}>
          <div className="flex items-center gap-2.5 px-4 py-3 bg-[#F8F6F3]">
            <sec.icon className="w-4 h-4" style={{ color: sec.color }} strokeWidth={1.25} />
            <p className="text-[10px] uppercase tracking-[0.15em] font-['Work_Sans']" style={{ color: sec.color }}>{sec.label}</p>
          </div>
          <div className="px-4 py-3 space-y-2">
            {links[sec.key].map((item, i) => (
              <button
                key={i}
                onClick={() => navigate(item.nav)}
                className="w-full flex items-center gap-3 text-left group py-1.5 hover:bg-[#F8F6F3] rounded-lg px-2 -mx-2 transition-colors"
                data-testid={`link-item-${sec.key}-${i}`}
              >
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: sec.color + '50' }} />
                <p className="text-[13px] text-[#4A4745] font-['Work_Sans'] leading-relaxed flex-1">{item.text}</p>
                <ExternalLink className="w-3 h-3 text-[#8A8681] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// HELPFUL REACTIONS
// ══════════════════════════════════════════════════════════════════════════════

const REACTIONS = [
  { key: 'helpful', label: 'Helpful', emoji: '✓' },
  { key: 'need_simpler', label: 'Need simpler explanation', emoji: '?' },
  { key: 'more_like_this', label: 'More like this', emoji: '+' },
];

function ReactionBar({ contentId }) {
  const [myReactions, setMyReactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/knowledge/reactions/${contentId}`)
      .then(r => setMyReactions(r.data?.my_reactions || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [contentId]);

  const toggle = async (key) => {
    try {
      const res = await axios.post(`${API}/knowledge/reactions/${contentId}`, { reaction_type: key });
      if (res.data.toggled) {
        setMyReactions(prev => [...prev, key]);
      } else {
        setMyReactions(prev => prev.filter(r => r !== key));
      }
    } catch { /* empty */ }
  };

  if (loading) return null;

  return (
    <div className="py-4" data-testid="reaction-bar">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mb-3">Was this helpful?</p>
      <div className="flex flex-wrap gap-2">
        {REACTIONS.map(r => {
          const active = myReactions.includes(r.key);
          return (
            <button
              key={r.key}
              onClick={() => toggle(r.key)}
              className={`px-4 py-2 rounded-full text-xs font-['Work_Sans'] transition-all duration-200 border ${
                active
                  ? 'bg-[#8C6B55]/10 border-[#8C6B55]/30 text-[#8C6B55]'
                  : 'bg-white border-[#EBE8E3] text-[#5C5955] hover:border-[#8C6B55]/20 hover:text-[#8C6B55]'
              }`}
              data-testid={`reaction-${r.key}`}
            >
              <span className="mr-1.5">{r.emoji}</span>{r.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// KNOWLEDGE DETAIL PAGE
// ══════════════════════════════════════════════════════════════════════════════

function KnowledgeDetail({ contentId, onBack }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/knowledge/detail/${contentId}`)
      .then(r => setItem(r.data))
      .catch(() => toast.error('Content not found'))
      .finally(() => setLoading(false));
  }, [contentId]);

  // Track progress on open
  useEffect(() => {
    if (!item) return;
    axios.post(`${API}/knowledge/progress/${contentId}`, {}).catch(() => {});
  }, [contentId, item]);

  // Track article scroll progress
  useEffect(() => {
    if (!item) return;
    let maxScroll = 0;
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const pct = Math.round((scrollTop / docHeight) * 100);
      if (pct > maxScroll + 10) {
        maxScroll = pct;
        axios.post(`${API}/knowledge/progress/${contentId}`, { article_progress_pct: pct }).catch(() => {});
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [contentId, item]);

  const toggleBookmark = async () => {
    const res = await axios.post(`${API}/knowledge/bookmark/${contentId}`);
    setItem(prev => ({ ...prev, is_bookmarked: res.data.bookmarked }));
  };

  if (loading) return <div className="max-w-3xl mx-auto py-20 text-center"><Loader2 className="w-6 h-6 text-[#C89B7B] animate-spin mx-auto" /></div>;
  if (!item) return null;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="knowledge-detail">
      {/* Hero header */}
      <div className="mb-8">
        <button onClick={onBack} className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#8A8681] hover:text-[#5C5955] transition-colors font-['Work_Sans'] mb-6" data-testid="back-btn">
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />Back to Knowledge
        </button>

        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <Badge variant="outline" className="border-[#EBE8E3] text-[#8A8681] text-[9px] capitalize mb-2">{item.category}</Badge>
            <h1 className="text-2xl sm:text-3xl font-['Outfit'] font-light text-[#2C2A29] leading-tight" data-testid="detail-title">{item.title}</h1>
            {item.subtitle && <p className="text-sm text-[#5C5955] font-['Work_Sans'] mt-1">{item.subtitle}</p>}
          </div>
          <button onClick={toggleBookmark} className="mt-2 p-2 rounded-full hover:bg-[#F4F2EE] transition-colors flex-shrink-0" data-testid="bookmark-btn">
            {item.is_bookmarked ? <BookmarkCheck className="w-5 h-5 text-[#8C6B55]" /> : <Bookmark className="w-5 h-5 text-[#8A8681]" />}
          </button>
        </div>
        {item.context_line && (
          <p className="text-xs text-[#8C6B55] font-['Work_Sans'] italic px-4 py-2.5 rounded-xl bg-[#8C6B55]/[0.04] border-l-2 border-[#8C6B55]/20">{item.context_line}</p>
        )}
      </div>

      {/* Slideshow */}
      {item.slide_images?.length > 0 && (
        <div className="mb-10">
          <SlideCarousel slides={item.slide_images} contentId={contentId} />
        </div>
      )}

      {/* Why this matters in your case — data-linked */}
      {item.why_this_matters?.has_data_link && item.why_this_matters?.bullets?.length > 0 && (
        <div className="mb-10 p-5 rounded-2xl border-l-[3px] border-[#4E7A6B]/40 bg-[#4E7A6B]/[0.03]" data-testid="why-this-matters">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#4E7A6B]/70 font-['Work_Sans'] mb-3">
            Why this matters in your case
          </p>
          <div className="space-y-2.5">
            {item.why_this_matters.bullets.map((b, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#4E7A6B] mt-[7px] flex-shrink-0" />
                <p className="text-sm text-[#2C2A29] font-['Work_Sans'] leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content Links — 4-layer data linking */}
      {item.content_links?.has_links && (
        <ContentLinksBlock links={item.content_links} navigate={navigate} />
      )}

      {/* Key Takeaways */}
      {item.takeaways?.length > 0 && (
        <div className="mb-10 p-5 rounded-2xl bg-[#F8F6F3] border border-[#EBE8E3]" data-testid="takeaways-section">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mb-4">Key Takeaways</p>
          <div className="space-y-2.5">
            {item.takeaways.map((t, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-[#C89B7B]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[9px] font-medium text-[#C89B7B]">{i + 1}</span>
                </div>
                <p className="text-sm text-[#2C2A29] font-['Work_Sans'] leading-relaxed">{t}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Article Sections */}
      {item.sections?.length > 0 && (
        <div className="space-y-8 mb-10" data-testid="article-sections">
          {item.sections.filter(s => s.body?.trim()).map((s, i) => (
            <div key={i} data-testid={`article-section-${i}`}>
              {s.title && (
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#8C6B55]/60 font-['Work_Sans'] mb-3">{s.title}</p>
              )}
              <p className="text-[15px] text-[#4A4745] leading-[1.9] font-['Work_Sans'] font-light whitespace-pre-line">{s.body}</p>
            </div>
          ))}
        </div>
      )}

      {/* Audio Player */}
      {item.audio_url && (
        <div className="mb-10">
          <AudioPlayer url={item.audio_url} />
        </div>
      )}

      {/* Reactions */}
      <ReactionBar contentId={contentId} />

      {/* Mark as completed */}
      <div className="flex justify-center py-4">
        <button
          onClick={async () => {
            await axios.post(`${API}/knowledge/progress/${contentId}`, { mark_completed: true });
            toast.success('Marked as completed');
          }}
          className="text-xs text-[#4E7A6B] font-['Work_Sans'] hover:text-[#3E6A5B] transition-colors px-4 py-2 rounded-full border border-[#4E7A6B]/20 hover:bg-[#4E7A6B]/5"
          data-testid="mark-completed-btn"
        >
          Mark as completed
        </button>
      </div>

      {/* Comment Thread */}
      <div className="pt-6 border-t border-[#EBE8E3]">
        <CommentThread contentId={contentId} userId={user?.id} />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN KNOWLEDGE PAGE
// ══════════════════════════════════════════════════════════════════════════════

export default function Knowledge() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeContentId, setActiveContentId] = useState(searchParams.get('topic') || null);

  useEffect(() => {
    axios.get(`${API}/knowledge/feed`)
      .then(r => setItems(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openDetail = (id) => {
    setActiveContentId(id);
    setSearchParams({ topic: id });
    window.scrollTo(0, 0);
  };

  const closeDetail = () => {
    setActiveContentId(null);
    setSearchParams({});
  };

  if (activeContentId) {
    return (
      <BiologicalLayout>
        <KnowledgeDetail contentId={activeContentId} onBack={closeDetail} />
      </BiologicalLayout>
    );
  }

  return (
    <BiologicalLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="knowledge-home">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#8A8681] font-['Work_Sans'] mb-3">
            Learn
          </p>
          <h1 className="text-2xl sm:text-3xl font-['Outfit'] font-light text-[#2C2A29] leading-tight mb-2" data-testid="knowledge-heading">
            Learn Your Biology
          </h1>
          <p className="text-sm text-[#7C7A77] font-['Work_Sans'] font-light max-w-lg mx-auto">
            Understand the biology behind your journey. Selected for your current state and pathway.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-[#C89B7B] animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20" data-testid="knowledge-empty">
            <div className="w-16 h-16 rounded-full bg-[#F4F2EE] flex items-center justify-center mx-auto mb-4">
              <BookmarkCheck className="w-7 h-7 text-[#C89B7B]" strokeWidth={1} />
            </div>
            <h2 className="text-lg font-['Outfit'] font-light text-[#2C2A29] mb-2">Knowledge is being prepared</h2>
            <p className="text-sm text-[#8A8681] font-['Work_Sans']">Your care team is crafting content tailored to your journey.</p>
          </div>
        ) : (
          <div className="space-y-10" data-testid="knowledge-sections">
            {/* Bucketed sections */}
            {[
              { key: 'continue_learning', label: 'Continue Learning', desc: 'Pick up where you left off' },
              { key: 'recommended', label: 'Recommended for You', desc: 'Matched to your condition and patterns' },
              { key: 'current_pattern', label: 'Because of Your Current Pattern', desc: 'Linked to your symptoms, medication, or progress' },
              { key: 'recent', label: 'Recently Added', desc: 'Latest knowledge from your care team' },
            ].map(section => {
              const sectionItems = items.filter(i => i.section_bucket === section.key);
              if (sectionItems.length === 0) return null;
              return (
                <div key={section.key} data-testid={`section-${section.key}`}>
                  <div className="mb-4">
                    <h2 className="text-lg font-['Outfit'] font-light text-[#2C2A29]">{section.label}</h2>
                    <p className="text-xs text-[#8A8681] font-['Work_Sans'] mt-0.5">{section.desc}</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {sectionItems.map(item => (
                      <KnowledgeCard key={item.id} item={item} onClick={() => openDetail(item.id)} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </BiologicalLayout>
  );
}
